import { API_BASE_URL } from '../config/api';
export interface FastTrackResult {
  handled: boolean;
  message?: string;
  navigationHref?: string;
  continueToServer?: boolean;
  action?: string;
}

const getAuthContext = () => {
  const token = localStorage.getItem('token');
  const businessIdStr = localStorage.getItem('businessId');
  const businessId = businessIdStr ? parseInt(businessIdStr, 10) : undefined;
  if (!token || !businessId) throw new Error('Not authenticated or no business selected');
  return { token, businessId };
};

const findContactFuzzy = async (token: string, businessId: number, name: string, type: string): Promise<number | null> => {
  try {
    const searchResp = await fetch(
      `${API_BASE_URL}/contacts?business_id=${businessId}&search=${encodeURIComponent(name)}&type=${type}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (searchResp.ok) {
      const searchJson = await searchResp.json();
      const contacts = searchJson?.data?.contacts || [];
      if (contacts.length > 0) return contacts[0].id;
    }
    const fuzzyResp = await fetch(`${API_BASE_URL}/contacts/fuzzy-match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ business_id: businessId, vendor_name: name, type })
    });
    if (fuzzyResp.ok) {
      const fuzzyJson = await fuzzyResp.json();
      return fuzzyJson?.data?.contact?.id || null;
    }
  } catch {
    return null;
  }
  return null;
};

const createContact = async (token: string, businessId: number, name: string, type: 'customer' | 'supplier') => {
  const response = await fetch(`${API_BASE_URL}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      business_id: businessId,
      contact_type: type,
      company_name: name,
      contact_name: name,
      email: `auto-${Date.now()}-${Math.random().toString(36).slice(2, 8)}@${type}.local`
    })
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

export const tryClientNavigationFastTrack = (text: string): FastTrackResult | null => {
  const navPatterns = [
    {
      pattern: /(?:go to|take me to|navigate to|open|show me)\s+(?:the\s+)?(dashboard|contacts|invoices|quotes|quotations|quotation|expenses|reports|settings)/i,
      page: (match: RegExpMatchArray) => {
        const dest = match[1].toLowerCase();
        if (dest === 'quotations' || dest === 'quotation') return '/quotes';
        return `/${dest}`;
      },
    },
    { pattern: /^(dashboard|contacts|invoices|quotes|expenses|reports|settings)$/i, page: (match: RegExpMatchArray) => `/${match[1]}` }
  ];
  for (const { pattern, page } of navPatterns) {
    const match = text.match(pattern);
    if (match) return { handled: true, message: `Navigating to ${match[1]}`, navigationHref: page(match) };
  }
  return null;
};

export const tryCreateExpenseFastTrack = async (text: string): Promise<FastTrackResult | null> => {
  const textLower = text.toLowerCase();
  const isBlockedCommand =
    /(inventory|stock|units?\s+to\s+inventory|add\s+\d+\s+units?\s+to)/i.test(textLower) ||
    /(bank|deposit|withdrawal|withdraw|transfer\s+money|move\s+funds)/i.test(textLower) ||
    /(invoice|bill\s+customer|send\s+invoice|create\s+invoice)/i.test(textLower) ||
    /(bill\s+from|purchase\s+bill|supplier\s+bill|create\s+bill)/i.test(textLower) ||
    /(payment|pay\s+to|receive\s+payment|record\s+payment)/i.test(textLower);
  if (isBlockedCommand) return null;

  const amountMatch = text.replace(/[,]/g, '').match(/(?:aed|usd|eur|\$)?\s*([0-9]+(?:\.[0-9]{1,2})?)/i);
  const hasSpentVerb = /(spent|spend|spending|pay|paid|payment|record|add|log)\b/i.test(textLower);
  const expenseKeywords = /(expense|taxi|fuel|restaurant|meal|meals|parking|hotel|travel|software|subscription|utilities|marketing|equipment)/i.test(textLower);
  if (!amountMatch || (!hasSpentVerb && !expenseKeywords)) return null;

  try {
    const { token, businessId } = getAuthContext();
    const amount = parseFloat(amountMatch[1]);
    const vendor = text.match(/(?:at|from)\s+([\p{L}0-9 &.'-]{2,})/iu)?.[1].trim() || 'Voice Entry';
    const category = guessExpenseCategory(textLower);
    let contactId = await findContactFuzzy(token, businessId, vendor, 'supplier');
    if (!contactId) {
      await createContact(token, businessId, vendor, 'supplier').catch(() => undefined);
      contactId = await findContactFuzzy(token, businessId, vendor, 'supplier');
    }

    const resp = await fetch(`${API_BASE_URL}/expenses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        business_id: businessId,
        amount,
        total_amount: amount,
        currency: 'AED',
        description: `${category} expense`,
        category,
        vendor,
        contact_id: contactId || undefined,
        expense_date: new Date().toISOString().split('T')[0],
        is_reimbursable: false,
        is_billable: false
      })
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return { handled: true, message: `Recorded ${category} expense of ${amount} for ${vendor}.`, action: 'create_expense' };
  } catch (error) {
    return { handled: false, continueToServer: true, message: `Failed to record expense: ${error instanceof Error ? error.message : 'Unknown error'}` };
  }
};

export const tryCreateInvoiceFastTrack = async (text: string): Promise<FastTrackResult | null> => {
  const invoicePatterns = [
    /create\s+(?:a\s+)?(?:sales\s+)?invoice\s+for\s+([^0-9]+?)\s+(\d+(?:\.\d{1,2})?)/i,
    /create\s+(?:a\s+)?invoice\s+for\s+([^0-9]+?)\s+(\d+(?:\.\d{1,2})?)/i,
    /bill\s+([^0-9]+?)\s+(\d+(?:\.\d{1,2})?)/i,
    /sales\s+invoice\s+([^0-9]+?)\s+(\d+(?:\.\d{1,2})?)/i,
    /invoice\s+([^0-9]+?)\s+(\d+(?:\.\d{1,2})?)/i,
    /create\s+(?:a\s+)?invoice\s+([^0-9]+?)\s+(\d+(?:\.\d{1,2})?)/i
  ];
  const match = invoicePatterns.map((pattern) => text.match(pattern)).find(Boolean);
  if (!match) return null;

  try {
    const { token, businessId } = getAuthContext();
    const customerName = match[1].trim();
    const amount = parseFloat(match[2]);
    let contactId = await findContactFuzzy(token, businessId, customerName, 'customer');
    if (!contactId) {
      await createContact(token, businessId, customerName, 'customer');
      contactId = await findContactFuzzy(token, businessId, customerName, 'customer');
    }
    if (!contactId) throw new Error('Could not find or create customer');

    const invoiceResp = await fetch(`${API_BASE_URL}/sales-invoices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        business_id: businessId,
        contact_id: contactId,
        amount,
        description: `Invoice for ${customerName}`,
        invoice_date: new Date().toISOString().split('T')[0],
        invoice_type: 'sales',
        status: 'draft'
      })
    });
    if (!invoiceResp.ok) throw new Error(`HTTP ${invoiceResp.status}`);
    const invoiceJson = await invoiceResp.json();
    return {
      handled: true,
      message: `Created sales invoice for ${customerName} with amount ${amount}. Invoice #${invoiceJson?.data?.invoice?.id || 'N/A'}`,
      action: 'create_sales_invoice'
    };
  } catch (error) {
    return { handled: false, continueToServer: true, message: `Failed to create invoice: ${error instanceof Error ? error.message : 'Unknown error'}` };
  }
};

export const tryCreateContactFastTrack = async (text: string): Promise<FastTrackResult | null> => {
  const match = text.match(/(?:create|add|new)\s+(customer|supplier|contact)\s+([\p{L}0-9 &.'-]{2,})$/iu);
  if (!match) return null;

  try {
    const { token, businessId } = getAuthContext();
    const requestedType = match[1].toLowerCase();
    const contactType = requestedType === 'supplier' ? 'supplier' : 'customer';
    const name = match[2].trim();
    await createContact(token, businessId, name, contactType);
    return { handled: true, message: `Created ${contactType} contact for ${name}.`, action: 'create_contact' };
  } catch (error) {
    return { handled: false, continueToServer: true, message: `Failed to create contact: ${error instanceof Error ? error.message : 'Unknown error'}` };
  }
};

const guessExpenseCategory = (textLower: string): string => {
  if (/taxi|cab|uber|ride/i.test(textLower)) return 'Taxi';
  if (/fuel|gas|petrol|diesel/i.test(textLower)) return 'Fuel';
  if (/meal|restaurant|dinner|lunch|breakfast|coffee/i.test(textLower)) return 'Meals and Entertainment';
  if (/hotel/i.test(textLower)) return 'Hotel';
  if (/parking/i.test(textLower)) return 'Parking';
  if (/software|subscription|saas/i.test(textLower)) return 'Software';
  if (/utilities|dewa|bill/i.test(textLower)) return 'Utilities';
  if (/equipment|hardware/i.test(textLower)) return 'Equipment';
  if (/travel|flight|airline|ticket/i.test(textLower)) return 'Travel';
  return 'Other';
};
